import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    ContentChild,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';

import { BaseComponent } from '@ridango/core';
import { Inject } from '@angular/core';

import { Subject, timer } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { IconType, LatLngType, LayerGroupType, MapApiService, MapEvents, MarkerType } from '../services';
import { MapIconComponent } from './map-icon.component';
import { MapPopupComponent } from './map-popup.component';
import Env from '@ridango/core/env';
import { TranslateService } from '@ngx-translate/core';

export interface MarkerItem {
    latlng: LatLngType;
    name?: string;
}

@Component({
    selector: 'rwidget-map-marker-group',
    template: ``,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapMarkerGroupComponent extends BaseComponent implements OnInit, OnDestroy, AfterContentInit {
    private readonly MARKER_Z_INDEX_OFFSET_STEP = 10;

    private mapReady$ = this.mapApi.mapReady().pipe(filter((ready) => ready));

    private groupMarkersLayer: LayerGroupType;

    private markersLoaded = new Set();
    private markerZIndex = 0;

    private changes = new Subject<void>();

    @Input() deleteOld = false;
    @Input() shouldDecreaseMarkersZIndex = false;

    @Input() _markers: MarkerItem[] = [];
    private createdMarkers: MarkerType[] = [];

    @Input() set markers(m: MarkerItem[]) {
        this._markers = m;
        this.changes.next();
    }

    @Input() set iconZoomSize(value) {
        if (!this.groupMarkersLayer) {
            return;
        }

        this.changes.next();
    }

    @Output() markerClicked = new EventEmitter<any>();

    @ContentChild(MapPopupComponent) popupComponent: MapPopupComponent;

    @ContentChild(MapIconComponent) mapIconComponent: MapIconComponent;

    constructor(
        private mapApi: MapApiService,
        private zone: NgZone,
        private translate: TranslateService,
        @Inject('ENV') private env: Env
    ) {
        super();
    }

    ngOnInit(): void {
        this.groupMarkersLayer = this.mapApi.createMarkerClusterGroup({
            removeOutsideVisibleBounds: false,
            showCoverageOnHover: false,
            spiderfyOnMaxZoom: false,
            disableClusteringAtZoom: this.env.MAP?.CLUSTER_STOP_ICONS_ZOOM ?? 1,
            chunkedLoading: true
        });
    }

    ngAfterContentInit(): void {
        this.mapReady$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => this.init());
        this.changes.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
            timer(10)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(() => {
                    if (this.deleteOld) {
                        this.groupMarkersLayer.eachLayer((layer) => this.mapApi.removeLayer(layer));
                        this.groupMarkersLayer.clearLayers();
                        this.markersLoaded.clear();
                    }
                    this.zone.runOutsideAngular(() => this.addMarkersToGroup());
                });
        });
    }

    ngOnDestroy(): void {
        this.zone.runOutsideAngular(() => {
            this.createdMarkers.forEach((marker) => {
                marker.removeEventListener('click');
                marker.remove();
            });
            this.mapApi.removeLayerGroup(this.groupMarkersLayer);
        });
        super.ngOnDestroy();
    }

    private init() {
        this.zone.runOutsideAngular(() => {
            this.addMarkersToGroup();
            this.createEventsForUpdatingMarkers();
        });
    }

    addMarkersToGroup() {
        this._markers
            ?.filter((marker) => {
                return this.mapApi.isWithinMapBounds(marker.latlng);
            })
            .forEach((element) => {
                if (!this.markersLoaded.has(element)) {
                    const marker = this.createMarker(element);
                    this.createdMarkers.push(marker);
                    this.mapApi.addToLayerGroup(this.groupMarkersLayer, marker);
                    this.markersLoaded.add(element);
                }
            });

        this.addAriaLabelToGroup();
    }

    private addAriaLabelToGroup() {
        const polylinePoint = document.querySelectorAll('.leaflet-marker-icon');
        const currentUserLocation = document.querySelector('.leaflet-control-locate-location');

        polylinePoint?.forEach((marker, index) => {
            if (!marker?.getAttribute('alt')) {
                const translatedLabel = this.translate.instant('MAP_MARKER_FOR_POINT_OF_INTEREST', {
                    index: index + 1
                });
                marker?.setAttribute('aria-label', translatedLabel);
            }
            if (!marker?.getAttribute('aria-expanded')) {
                marker?.setAttribute('aria-expanded', 'false');
            }
        });
        const currentLocationLabel = this.translate.instant('MAP_MARKER_FOR_CURRENT_LOCATION');
        currentUserLocation?.setAttribute('aria-label', currentLocationLabel);
    }

    createEventsForUpdatingMarkers() {
        this.mapApi.addListener(MapEvents.moveEnd, this.addMarkersToGroup.bind(this, true));
        this.mapApi.addListener(MapEvents.zoomEnd, this.addMarkersToGroup.bind(this));
    }

    getMarkerGroup() {
        return this.groupMarkersLayer;
    }

    private createMarkerWithOptions(markerItem: MarkerItem): MarkerType {
        return this.mapApi.createMarker(markerItem.latlng, {
            zIndexOffset: this.getMarkersZIndex()
        });
    }

    private getMarkersZIndex(): number {
        this.markerZIndex += this.MARKER_Z_INDEX_OFFSET_STEP;

        return this.shouldDecreaseMarkersZIndex ? 0 : this.markerZIndex;
    }

    private createMarker(markerItem: MarkerItem): MarkerType {
        return this.zone.runOutsideAngular(() => {
            const marker = this.createMarkerWithOptions(markerItem);
            const icon = this.getMarkerIcon();
            marker.addEventListener('click', (event) => {
                // Set all other markers to aria-expanded=false
                const polylinePoint = document.querySelectorAll('.leaflet-marker-icon');
                polylinePoint?.forEach((point: HTMLElement) => {
                    point.setAttribute('aria-expanded', 'false');
                });

                // Set current marker to aria-expanded=true/false based on popup open state
                const target = event.originalEvent.target as HTMLElement;
                target.setAttribute('aria-expanded', (!event.target.isPopupOpen()).toString());
                this.markerClicked.emit(markerItem);
            });
            if (markerItem?.name) {
                marker.options.alt = `${this.translate.instant('OPEN_BUS_STOP')} ${markerItem.name}`;
            }
            this.mapApi.setMarkerIcon(marker, icon);
            this.bindPopup(marker);

            return marker;
        });
    }

    private getMarkerIcon(): IconType {
        if (!this.mapIconComponent) {
            throw new Error('rwidget-marker must contain rwidget-icon');
        }
        return this.mapIconComponent.getIcon();
    }

    private bindPopup(marker: MarkerType): void {
        if (this.popupComponent) {
            this.popupComponent.setHostMarker(marker);
        }
    }
}
